import { ArrowRight, Lightbulb, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

// =================================================================================================
// InsightCallout — the "so what" primitive.
//
// A prominent takeaway banner pinned to the TOP of a surface. It answers two questions before the
// reader scans a single chart: "what matters right now?" and "where do I look?". Compute the
// `headline` and `stats` from the loaded payload — never hardcode a number that could go stale.
//
// USAGE (drop-in at the top of a page, under <PageHeader>):
//
//   <InsightCallout
//     headline={`${critCount} devices are CRITICAL — concentrated in ${worstDistrict}.`}
//     stats={[
//       { label: "% critical", value: formatPct(criticalSharePct) },
//       { label: "AR exposure", value: formatUsdCompact(arUsd) },
//     ]}
//     severity="critical"            // omit (or "neutral"/"info") unless this IS a health signal
//     cta={{ label: "Open Fleet Health", to: "/health" }}
//   />
//
// COLOR LAW: `severity` accepts RAG tiers ("ok" | "warn" | "critical") ONLY when the headline is a
// genuine health/risk statement. For everything else use "neutral" (default) or "info" — these map
// to brand/surface tokens, never to RAG. This keeps red/amber/green meaningful.
// =================================================================================================

export type InsightSeverity = "neutral" | "info" | "ok" | "warn" | "critical";

export interface InsightStat {
  readonly label: string;
  // Pre-formatted display string (run it through lib/format first). tabular-nums is applied here.
  readonly value: string;
}

export interface InsightCta {
  readonly label: string;
  // Internal react-router path, e.g. "/health". External links are intentionally unsupported.
  readonly to: string;
}

export interface InsightCalloutProps {
  // The single most important takeaway, as a full sentence.
  readonly headline: string;
  // 1–3 supporting figures. More than three dilutes the "one thing that matters" framing.
  readonly stats?: readonly InsightStat[];
  // Accent. RAG values are health-only (see COLOR LAW above). Defaults to "neutral".
  readonly severity?: InsightSeverity;
  // Optional "where to look" link rendered as a button on the right.
  readonly cta?: InsightCta;
  // Optional leading glyph. Defaults to a lightbulb.
  readonly icon?: LucideIcon;
}

// Maps severity → the CSS modifier suffix on `.insight-callout`. RAG tiers resolve to RAG tokens in
// CSS; neutral/info resolve to brand/surface tokens. The mapping lives in CSS, not inline styles.
const SEVERITY_CLASS: Record<InsightSeverity, string> = {
  neutral: "insight-neutral",
  info: "insight-info",
  ok: "insight-ok",
  warn: "insight-warn",
  critical: "insight-critical",
};

export const InsightCallout = ({
  headline,
  stats,
  severity = "neutral",
  cta,
  icon: Icon = Lightbulb,
}: InsightCalloutProps): ReactNode => (
  <section className={`insight-callout ${SEVERITY_CLASS[severity]}`} aria-label="Key insight">
    <span className="insight-icon" aria-hidden>
      <Icon size={18} />
    </span>
    <div className="insight-body">
      <p className="insight-headline">{headline}</p>
      {stats && stats.length > 0 ? (
        <div className="insight-stats">
          {stats.map((s) => (
            <span key={s.label} className="insight-stat">
              <span className="insight-stat-value br-tabular-nums">{s.value}</span>
              <span className="insight-stat-label">{s.label}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
    {cta ? (
      <Link className="insight-cta" to={cta.to}>
        {cta.label}
        <ArrowRight size={14} aria-hidden />
      </Link>
    ) : null}
  </section>
);
